import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Tooltip, Space, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusSquareOutlined, FileSearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import BackToListing from '@components/BackToListing';
import { dateFormat, getDateInISOFormat } from '@utils/formatters';
import Table from '@components/Table';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import moment from 'moment';
import CustomTag, { TagColors } from '@components/CustomTag';
import AddItemsToInvoice from '../AddItemsToInvoice';

const { Search } = Input;

const InvoiceActions = () => {
  const { t } = useTranslation(['invoices-actions', 'attributes']);

  const [createdByValue, setCreatedByValue] = useState('');
  const [protocolValue, setProtocolValue] = useState('');
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const navigate = useNavigate();

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['createdBy', 'protocol', 'startDate', 'endDate'],
  });

  const { createdBy, protocol, startDate, endDate, page, linesPerPage } =
    useMemo(() => searchParams, [searchParams]);

  useEffect(() => {
    setCreatedByValue(createdBy);
  }, [createdBy]);

  useEffect(() => {
    setProtocolValue(protocol);
  }, [protocol]);

  const { data } = useSwr(
    `/service-proxy/broker/invoice-actions/summary`,
    {
      params: {
        page: page && Number(page) - 1,
        linesPerPage,
        createdBy,
        protocol,
        startDate,
        endDate,
      },
    },
    { refreshInterval: 10 * 2000 },
  );

  const renderColumnStatus = useCallback(
    (_: any, record: any) => {
      const { nbQueued, nbSuccess, nbError } = record;

      const statusItems: {
        name: string;
        value: number;
        color?: TagColors;
      }[] = [
        {
          name: 'error',
          value: nbError,
          color: 'red',
        },
        {
          name: 'queue',
          value: nbQueued,
          color: 'blue',
        },
        {
          name: 'success',
          value: nbSuccess,
          color: 'green',
        },
      ];

      return (
        <div>
          {statusItems?.map(
            ({ name, value, color }) =>
              value > 0 && (
                <Tooltip key={name} title={t(`upload-items.${name}`)}>
                  <CustomTag color={color} style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTag>
                </Tooltip>
              ),
          )}
        </div>
      );
    },
    [t],
  );

  const columns = useMemo(
    () => [
      { title: t('table.protocol'), dataIndex: 'protocol' },
      {
        title: t('table.actionType'),
        dataIndex: 'type',
        render: (value: string) => <span>{t(`action-type.${value}`)}</span>,
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: (value: string) => dateFormat(value),
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('table.status'),
        dataIndex: 'nbSuccess',
        align: 'center' as const,
        render: renderColumnStatus,
      },
      {
        title: t('attributes:details'),
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: any) => (
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              navigate(`details?protocol=${record.protocol}`);
            }}
          />
        ),
      },
    ],
    [t, renderColumnStatus, navigate],
  );

  const handleDateChange = useCallback(
    (date: { startDate: string | null; endDate: string | null }) => {
      setSearchParams({
        startDate: getDateInISOFormat(date?.startDate),
        endDate: getDateInISOFormat(date?.endDate),
      });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.createdBy'),
        element: (
          <Search
            onSearch={value => setSearchParams({ createdBy: value })}
            onChange={({ target: { value } }) => setCreatedByValue(value)}
            value={createdByValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.protocol'),
        element: (
          <Search
            onSearch={value => setSearchParams({ protocol: value })}
            onChange={({ target: { value } }) => setProtocolValue(value)}
            value={protocolValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.date'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [
      t,
      createdByValue,
      protocolValue,
      handleDateChange,
      startDate,
      endDate,
      setSearchParams,
    ],
  );

  return (
    <>
      <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
        <AddItemsToInvoice
          visible={showAddItemsModal}
          onClose={() => setShowAddItemsModal(false)}
        />
      </RoleChecker>
      <Table
        title={
          <BackToListing style={{ margin: 0 }} title={t('title-action')} />
        }
        columns={columns}
        data={data?.content}
        loading={!data}
        onTableChange={setSearchParams}
        tableKey="invoiceActions"
        extraActions={
          <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
            <Tooltip title={t('new-items-in-list')}>
              <Button
                icon={<PlusSquareOutlined />}
                onClick={() => setShowAddItemsModal(true)}
              />
            </Tooltip>
          </RoleChecker>
        }
        mainExtraAction={
          <Space>
            <Divider orientation="center" type="vertical" />
            <Tooltip title={t('invoice-all-actions')}>
              <Button
                icon={<FileSearchOutlined />}
                onClick={() => navigate('details')}
              />
            </Tooltip>
          </Space>
        }
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          openByDefault: true,
          onClearFilters: () => clearSearchParams(),
        }}
      />
    </>
  );
};

export default InvoiceActions;
